<template>
  <DinamicModalV2
    ref="dinamic-modal"
    :content-class="donateModalContentClass"
    :content-right-class="donateModalContentRightClass"
    :content-list="modalContentList"
    :right-content-list="rightModalContentList"
    :active-content="activeContent"
    :show-back-button="showBackButton"
    @return-back="returnBack"
  >
    <template #activator="{ onClick }">
      <div ref="donateButton" class="donate-modal__activator">
        <UiButton
          color="accent"
          @click="() => (onClick(), openDonateModal(), trackInitiateCheckout())"
        >
          {{ $t('button.help') }}
        </UiButton>
      </div>
    </template>
  </DinamicModalV2>
</template>

<script setup>
import useProfileStore from '~/store/profile';
import useAnalyticsStore from '~/store/analytics';

import snakecaseKeys from 'snakecase-keys';
import camelcaseKeys from 'camelcase-keys';
import { recordProjectDonationSuccess } from '~/utils/paymentSuccessProjectDonations';

const LoginModal = defineAsyncComponent(
  () => import('~/components/Modals/Login.vue'),
);

const DonateFormModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/Form.vue'),
);
const HelpImastInfoModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/HelpImastInfo.vue'),
);
const HelpImastSelectPercentModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/HelpImastSelectPercent.vue'),
);
const PaymentTypesModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/PaymentTypes.vue'),
);
const StripeModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/Stripe.vue'),
);
const PaymentProcessModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/PaymentProcess.vue'),
);
const PaymentSuccessModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/PaymentSuccess/index.vue'),
);
const PaymentErrorModal = defineAsyncComponent(
  () => import('~/components/Modals/Donate/PaymentError.vue'),
);

const MODAL_CONTENT_TYPES = {
  LOGIN: 'login',
  FORM: 'form',
  HELP_INFO: 'helpInfo',
  HELP_PERCENT: 'helpPercent',
  PAYMENT_TYPES: 'paymentTypes',
  STRIPE_FORM: 'stripeForm',
  PAYMENT_PROCESS: 'paymentProcess',
  PAYMENT_SUCCESS: 'paymentSuccess',
  PAYMENT_ERROR: 'paymentError',
};

const appearance = {
  variables: {
    fontFamily: 'SF Pro, sans-serif',
    fontLineHeight: '20px',
    colorText: '#101114',
    colorTextPlaceholder: '#939495',
    colorPrimary: '#6E6EB8',
    colorDanger: '#DF1B41',
    borderRadius: '12px',
  },
  rules: {
    '.Input:focus': {
      boxShadow: 'none',
    },
    '.Label': {},
  },
};

const { $useApi } = useNuxtApp();

const {
  project,
  referrer,
  updateProject,
  openPromoError,
  openPaymentError,
  utmData,
} = defineProps({
  project: {
    type: Object,
    default: () => ({}),
  },
  referrer: {
    type: String,
    default: undefined,
  },
  updateProject: {
    type: Function,
    default: () => {},
  },
  openPromoError: {
    type: Function,
    default: () => {},
  },
  openPaymentError: {
    type: Function,
    default: () => {},
  },
  utmData: {
    type: Object,
    default: null,
  },
});
const stripe = ref(null);
let stripePromise = null;
const config = useRuntimeConfig();
const { $t } = useI18n();
const profileStore = useProfileStore();
const donateButton = useTemplateRef('donateButton');
const isAuth = computed(() => profileStore.isAuth);
const selectedCardId = computed(() => profileStore.getSelectedCardId);

const dinamicModalRef = useTemplateRef('dinamic-modal');

const donateModalContentClass = computed(() => [
  'donate-modal',
  { '--login': activeContent.value === MODAL_CONTENT_TYPES.LOGIN },
  { '--success': activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_SUCCESS },
]);

const donateModalContentRightClass = computed(() => [
  'donate-modal',
  { '--success': activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_SUCCESS },
]);

const elements = ref(null);
const paymentElement = ref(null);
const disablePaymentButton = ref(true);
const donateSum = ref(null);
const helpSum = ref({
  percent: 15,
  sum: null,
  type: 'percent', // percent | custom
});
const isAnonymous = ref(false);
const paymentErrorMessage = ref(null);
const googleAuth = useGoogleAuthRedirect();
const googleResumeLoading = ref(false);
const subscriptionsCount = ref(0);
const hidePulseByDonationCount = ref(false);
const totalDonateSum = computed(() => {
  if (helpSum.value.type === 'percent') {
    return donateSum.value
      ? Number(donateSum.value) +
          (Number(donateSum.value) * Number(helpSum.value.percent)) / 100
      : 0;
  }
  return donateSum.value
    ? Number(donateSum.value) + Number(helpSum.value.sum || 0)
    : 0;
});

const totalHelpSum = computed(() => {
  if (helpSum.value.type === 'percent') {
    const sum = donateSum.value
      ? ((donateSum.value / 100) * helpSum.value.percent).toFixed(2)
      : 0;

    return Number(sum) || 0;
  }
  return helpSum.value.sum || 0;
});

const modalContentList = computed(() => [
  {
    id: MODAL_CONTENT_TYPES.LOGIN,
    component: LoginModal,
    transitionName: 'fade-modal-content',
    props: {
      referrer,
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.LOGIN,
      sendUtmData,
      utmData,
      initialLoading: googleResumeLoading.value,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.FORM,
    component: DonateFormModal,
    transitionName: 'fade-modal-content',
    props: {
      project,
      stripe: stripe.value,
      donateSum: donateSum.value,
      totalDonateSum: totalDonateSum.value,
      'onUpdate:donateSum': (value) => (donateSum.value = value),
      helpSum: helpSum.value,
      isAnonymous: isAnonymous.value,
      'onUpdate:isAnonymous': (value) => (isAnonymous.value = value),
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.FORM,
      createCardPayment,
      openHelpImastSelectPercent,
      openHelpImastInfo,
      openPaymentTypesModal,
      successPayment,
      updateProject: updateProject,
      sendDonate,
      openPromoError,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.HELP_INFO,
    component: HelpImastInfoModal,
    transitionName: 'slide-modal-content',
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.HELP_IMAST,
      returnBack,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.HELP_PERCENT,
    component: HelpImastSelectPercentModal,
    transitionName: 'slide-modal-content',
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.HELP_PERCENT,
      donateSum: donateSum.value,
      helpSum: helpSum.value,
      'onUpdate:helpSum': (value) => (helpSum.value = value),
      returnBack,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.PAYMENT_TYPES,
    component: PaymentTypesModal,
    transitionName: 'fade-modal-content',
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_TYPES,
      returnBack,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.STRIPE_FORM,
    component: StripeModal,
    transitionName: 'fade-modal-content',
    props: {
      paymentElement: paymentElement.value,
      disablePaymentButton: disablePaymentButton.value,
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.STRIPE_FORM,
      confirmPayment,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.PAYMENT_PROCESS,
    component: PaymentProcessModal,
    transitionName: 'fade-modal-content',
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_PROCESS,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.PAYMENT_SUCCESS,
    component: PaymentSuccessModal,
    transitionName: 'fade-modal-content',
    splitAnimation: true,
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_SUCCESS,
      panel: 'left',
      projectCover: project.covers[0] || null,
      donateSum: totalDonateSum.value,
      subscriptionsCount: subscriptionsCount.value,
      fund: project.fundBasicData || null,
      hidePulseByDonationCount: hidePulseByDonationCount.value,
    },
  },
  {
    id: MODAL_CONTENT_TYPES.PAYMENT_ERROR,
    component: PaymentErrorModal,
    transitionName: 'fade-modal-content',
    props: {
      isVisible: activeContent.value === MODAL_CONTENT_TYPES.PAYMENT_ERROR,
      error: paymentErrorMessage.value,
      donateSum: totalDonateSum.value,
      project: project.name,
      selectedPaymentMethod: null,
      returnBack,
      repeatTransaction,
    },
  },
]);

const rightModalContentList = computed(() => [
  {
    id: MODAL_CONTENT_TYPES.PAYMENT_SUCCESS,
    component: PaymentSuccessModal,
    splitAnimation: true,
    props: {
      panel: 'right',
      fund: project.fundBasicData || null,
      hidePulseByDonationCount: hidePulseByDonationCount.value,
    },
  },
]);

const showBackButton = computed(() => {
  switch (activeContent.value) {
    case MODAL_CONTENT_TYPES.LOGIN:
      return false;
    case MODAL_CONTENT_TYPES.FORM:
      return false;
    case MODAL_CONTENT_TYPES.HELP_INFO:
      return true;
    case MODAL_CONTENT_TYPES.HELP_PERCENT:
      return true;
    case MODAL_CONTENT_TYPES.PAYMENT_TYPES:
      return true;
    default:
      return false;
  }
});

const activeContent = ref(null);

function updateBodyPaddingForDonateButton() {
  document.body.style.paddingBottom =
    window.innerWidth < 768 ? donateButton?.value?.scrollHeight + 'px' : null;
}

async function loadStripeInstance() {
  if (stripe.value) return stripe.value;

  if (!stripePromise) {
    stripePromise = (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        const instance = await loadStripe(config.public.STRIPE_ID);
        stripe.value = instance;

        if (!instance) {
          stripePromise = null;
        }

        return instance;
      } catch (error) {
        stripePromise = null;
        throw error;
      }
    })();
  }

  return stripePromise;
}

watch(activeContent, async () => {
  if (activeContent.value === MODAL_CONTENT_TYPES.FORM && !stripe.value) {
    try {
      await loadStripeInstance();
    } catch (error) {
      console.error(error);
    }
  }
});

watch(isAuth, () => {
  const wasLoginFlow = activeContent.value === MODAL_CONTENT_TYPES.LOGIN;

  activeContent.value = isAuth.value
    ? MODAL_CONTENT_TYPES.FORM
    : MODAL_CONTENT_TYPES.LOGIN;

  if (isAuth.value) {
    getPaymentMethods();
    if (wasLoginFlow) trackInitiateCheckout();
  } else {
    donateSum.value = null;
    helpSum.value = {
      percent: 15,
      sum: null,
      type: 'percent',
    };
  }
});

onMounted(() => {
  updateBodyPaddingForDonateButton();
  window.addEventListener('resize', updateBodyPaddingForDonateButton);
  openGoogleResume();

  if (profileStore.isAuth) {
    getPaymentMethods();
  }
});

function openGoogleResume() {
  const pending = googleAuth.peekResume();
  if (!pending) return;

  googleResumeLoading.value = Boolean(pending.code);
  activeContent.value = MODAL_CONTENT_TYPES.LOGIN;
  dinamicModalRef.value?.open?.();
}

onUnmounted(() => {
  window.removeEventListener('resize', updateBodyPaddingForDonateButton);
  document.body.style.paddingBottom = null;
});

async function getPaymentMethods() {
  try {
    const response = await $fetch($useApi.payments.getPaymentMethods, {
      headers: useCustomRequestHeaders(),
    });

    const { bankCards, defaultPaymentMethod, defaultBankCardId } =
      camelcaseKeys(response, { deep: true });

    profileStore.setPaymentMethods(bankCards);

    if (!bankCards.length) {
      return;
    }

    if (defaultPaymentMethod === 'bank_card') {
      profileStore.setSelectedCardId(defaultBankCardId);
    } else if (bankCards.some((method) => method.id === defaultPaymentMethod)) {
      profileStore.setSelectedCardId(defaultPaymentMethod);
    }
  } catch (error) {
    console.error(error);
  }
}

function getFbqProjectPayload() {
  return {
    content_ids: [String(project.id)],
    currency: 'USD',
    num_items: 0,
    value: 0,
  };
}

function trackInitiateCheckout() {
  if (!isAuth.value) return;

  useAnalyticsStore().trackFbq('InitiateCheckout', getFbqProjectPayload());
}

function openDonateModal() {
  activeContent.value = isAuth.value
    ? MODAL_CONTENT_TYPES.FORM
    : MODAL_CONTENT_TYPES.LOGIN;
}

function openHelpImastSelectPercent() {
  activeContent.value = MODAL_CONTENT_TYPES.HELP_PERCENT;
}

function openHelpImastInfo() {
  activeContent.value = MODAL_CONTENT_TYPES.HELP_INFO;
}

function openPaymentTypesModal() {
  activeContent.value = MODAL_CONTENT_TYPES.PAYMENT_TYPES;
}

function openPaymentProcessModal() {
  activeContent.value = MODAL_CONTENT_TYPES.PAYMENT_PROCESS;
}

function openStripeModal() {
  activeContent.value = MODAL_CONTENT_TYPES.STRIPE_FORM;
}

function openPaymentErrorModal() {
  activeContent.value = MODAL_CONTENT_TYPES.PAYMENT_ERROR;
}

function openPaymentSuccessModal() {
  activeContent.value = MODAL_CONTENT_TYPES.PAYMENT_SUCCESS;
  updateProject();
}

async function ensureStripe() {
  try {
    const instance = await loadStripeInstance();
    if (instance) return instance;
  } catch (error) {
    console.error(error);
  }

  paymentErrorMessage.value = $t('stripe_error.load_failed');
  openPaymentErrorModal();
  return null;
}

async function sendDonate() {
  switch (selectedCardId.value) {
    case 'stripe':
      return payByNewCard();
    default:
      return payBySavedCard();
  }
}

function repeatTransaction() {
  openDonateModal();
  sendDonate();
}

async function payByNewCard() {
  try {
    if (!(await ensureStripe())) return;

    const { paymentIntent } = await createCardPayment();
    elements.value = stripe.value.elements({
      clientSecret: paymentIntent,
      appearance,
    });
    paymentElement.value = await elements.value.create('payment');
    paymentElement.value.on(
      'change',
      (evt) => (disablePaymentButton.value = !evt.complete),
    );
    openStripeModal();
  } catch (error) {
    console.error(error);
  }
}

async function payBySavedCard() {
  openPaymentProcessModal();

  try {
    if (!(await ensureStripe())) return;

    const { paymentIntent } = await createCardPayment({
      paymentMethodId: selectedCardId.value,
    });

    if (paymentIntent) {
      confirmCardPayment({ clientSecret: paymentIntent });
    } else {
      openPaymentErrorModal();
    }
  } catch (error) {
    console.error(error);
  }
}

async function createCardPayment(
  { paymentMethodId, saveCard } = { saveCard: true },
) {
  const fbp = document.cookie.match(/_fbp=([^;]+)/)?.[1];
  const fbc = document.cookie.match(/_fbc=([^;]+)/)?.[1];

  const requestBody = {
    to: 'project',
    id: project.id,
    donateAmount: donateSum.value.toFixed(2) * 100,
    tipAmount: (totalHelpSum.value * 100).toFixed(0),
    isAnonymous: isAnonymous.value,
    saveCard,
    paymentMethodId,
    facebookData: {
      webData: {
        fbc: fbc ? fbc : '',
        fbp: fbp ? fbp : '',
      },
    },
  };

  try {
    const response = await $fetch($useApi.payments.createCardPayment, {
      method: 'POST',
      headers: useCustomRequestHeaders(),
      body: snakecaseKeys(requestBody, { deep: true }),
    });

    return camelcaseKeys(response, { deep: true });
  } catch (error) {
    if (error.response.status === 404) {
      openPaymentError();
    }

    return {};
  }
}

async function confirmCardPayment({ clientSecret, appleToken }) {
  const paymentMethod = appleToken
    ? {
        payment_method: {
          card: {
            token: appleToken,
          },
        },
      }
    : {};

  let isSuccessPayment = false;

  const response = stripe.value
    ?.confirmCardPayment(clientSecret, paymentMethod)
    .then(async (res) => {
      if (res.error) {
        paymentErrorMessage.value =
          res.error.code === 'insufficient_funds'
            ? t('stripe_error.insufficient_funds')
            : res.error.message;

        openPaymentErrorModal();
      } else {
        isSuccessPayment = true;
        successPayment();
      }

      return res;
    })
    .catch(() => {
      paymentErrorMessage.value = 'Error';
      openPaymentErrorModal();
    })
    .finally(() => {
      // isLoading.value = false;
    });

  if (isSuccessPayment) {
    await $fetch($useApi.payments.setDefaultPaymentMethods, {
      method: 'PUT',
      headers: useCustomRequestHeaders(),
      body: {
        default_payment_method: 'bank_card',
        bank_card_id: response.payment_method,
      },
    });
  }
}

async function sendUtmData() {
  if (!utmData) return;

  const body = snakecaseKeys(
    { ref: referrer, utmData: [{ ...utmData, at: `${Date.now()}` }] },
    { deep: true },
  );

  try {
    $fetch($useApi.marketing.sendUtmData, {
      method: 'POST',
      headers: useCustomRequestHeaders(),
      body,
    });
  } catch (error) {
    console.error(error);
  }
}

async function confirmPayment() {
  openPaymentProcessModal();

  if (!(await ensureStripe())) return;

  stripe.value
    .confirmPayment({
      elements: elements.value,
      redirect: 'if_required',
    })
    .then(async (res) => {
      if (res.error) {
        openStripeModal();
      } else {
        await $fetch($useApi.payments.setDefaultPaymentMethods, {
          method: 'PUT',
          headers: useCustomRequestHeaders(),
          body: {
            default_payment_method: 'bank_card',
            bank_card_id: res.paymentIntent.payment_method,
          },
        });
        successPayment();
      }
    })
    .finally(() => {
      // isLoading.value = false;
    });
}

async function successPayment() {
  getPaymentMethods();
  useAnalyticsStore().track('payment.success', {
    value: donateSum.value.toFixed(2),
    currency: 'USD',
  });
  useAnalyticsStore().track('purchase', {
    value: donateSum.value.toFixed(2),
    currency: 'USD',
  });
  useAnalyticsStore().trackFbq('Purchase', {
    value: donateSum.value.toFixed(2),
    currency: 'USD',
  });

  try {
    const response = await $fetch($useApi.profile.getPulseSubscriptions, {
      headers: useCustomRequestHeaders(),
    });

    subscriptionsCount.value = response.count;
  } catch (error) {
    console.log(error);
  } finally {
    const userId = profileStore.profile?.id;

    hidePulseByDonationCount.value =
      import.meta.client && userId
        ? recordProjectDonationSuccess(userId)
        : false;

    openPaymentSuccessModal();
  }
}

function returnBack() {
  activeContent.value = isAuth.value
    ? MODAL_CONTENT_TYPES.FORM
    : MODAL_CONTENT_TYPES.LOGIN;
}

defineExpose({
  dinamicModalRef,
  open: () => {
    if (dinamicModalRef.value && dinamicModalRef.value.open) {
      trackInitiateCheckout();
      activeContent.value = isAuth.value
        ? MODAL_CONTENT_TYPES.FORM
        : MODAL_CONTENT_TYPES.LOGIN;
      dinamicModalRef.value.open();
    }
  },
  close: () => {
    if (dinamicModalRef.value && dinamicModalRef.value.close) {
      dinamicModalRef.value.close();
    }
  },
});
</script>

<style lang="scss">
@use '@styles/components/pages/project/donateModal.scss' as *;
</style>
